import React, { useEffect, useState } from 'react';
import { Check, X, Users, Loader2, Radio, Info } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Vote QCM depuis le téléphone de l'élève connecté à une séance live (?viewer=1).
 * Un seul vote par question et par appareil (localStorage) pour ne pas fausser les stats.
 */
export default function PhoneQcmVote({ slide, pollSessionId, slug, qIndex, revealed }) {
  const storageKey = `cv_phone_vote:${pollSessionId}:${slug}:${qIndex}`;
  const [picked, setPicked] = useState(() => {
    const v = localStorage.getItem(storageKey);
    return v === null ? null : parseInt(v, 10);
  });
  const [role, setRole] = useState(localStorage.getItem('cv_voter_role') || 'eleve');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => { localStorage.setItem('cv_voter_role', role); }, [role]);

  // Compteur de votes en direct (léger, 2.5s)
  useEffect(() => {
    let alive = true;
    const fetchTotal = async () => {
      try {
        const r = await axios.get(`${API}/poll/results/${pollSessionId}/${slug}/${qIndex}`);
        if (alive) setTotal(r.data?.total || 0);
      } catch { /* silent */ }
    };
    fetchTotal();
    const iv = setInterval(fetchTotal, 2500);
    return () => { alive = false; clearInterval(iv); };
  }, [pollSessionId, slug, qIndex]);

  const vote = async (choice) => {
    if (picked !== null || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/poll/vote`, { session_id: pollSessionId, slug, q_index: qIndex, choice, voter_role: role });
      localStorage.setItem(storageKey, String(choice));
      setPicked(choice);
      try { navigator.vibrate?.(30); } catch { /* noop */ }
    } catch {
      setError("Erreur d'envoi. Réessaie.");
    } finally {
      setSubmitting(false);
    }
  };

  const myAnswerCorrect = picked !== null && !!slide.options[picked]?.correct;

  return (
    <div className="min-h-full flex flex-col px-5 py-6 overflow-y-auto" data-testid="phone-qcm-vote">
      <div className="flex items-center justify-between gap-2 mb-4">
        <span className="inline-flex items-center gap-1.5 text-red-400 text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
          <Radio size={11} /> QCM en direct
        </span>
        {total > 0 && (
          <span className="text-emerald-300 text-[11.5px] font-mono-spec font-bold" data-testid="phone-vote-total">{total} vote{total > 1 ? 's' : ''}</span>
        )}
      </div>

      {picked === null && !revealed && (
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <Users size={13} className="text-white/40" />
          <span className="text-white/60 text-[12px]">Tu es :</span>
          <button onClick={() => setRole('eleve')} data-testid="phone-vote-role-eleve" className={`h-8 px-3 rounded-full text-[12px] transition ${role === 'eleve' ? 'bg-sky-500 text-black font-bold' : 'border border-white/15 text-white/70'}`}>Élève</button>
          <button onClick={() => setRole('parent')} data-testid="phone-vote-role-parent" className={`h-8 px-3 rounded-full text-[12px] transition ${role === 'parent' ? 'bg-amber-500 text-black font-bold' : 'border border-white/15 text-white/70'}`}>Accompagnateur</button>
        </div>
      )}

      <h1 className="font-serif-editorial text-white text-[21px] leading-snug mb-5">{slide.question}</h1>

      <div className="space-y-3">
        {slide.options.map((opt, i) => {
          const isMine = picked === i;
          const showCorrect = revealed && opt.correct;
          const showWrong = revealed && isMine && !opt.correct;
          return (
            <button
              key={i}
              type="button"
              onClick={() => vote(i)}
              disabled={picked !== null || submitting || revealed}
              data-testid={`phone-vote-opt-${i}`}
              className={`w-full text-left rounded-xl border p-4 flex items-start gap-3 transition ${
                showCorrect ? 'border-emerald-500 bg-emerald-950/40 text-emerald-100' :
                showWrong ? 'border-red-500 bg-red-950/40 text-red-100' :
                isMine ? 'border-sky-400 bg-sky-950/40 text-white' :
                picked !== null || revealed ? 'border-white/10 bg-white/[0.03] text-white/45' :
                'border-white/15 bg-white/[0.04] active:border-red-500/60 active:bg-red-950/30 text-white'
              }`}
            >
              <span className={`shrink-0 w-8 h-8 rounded-full border font-mono-spec text-[13px] flex items-center justify-center ${
                showCorrect ? 'border-emerald-400 bg-emerald-500/25 text-emerald-100' :
                showWrong ? 'border-red-400 bg-red-500/20 text-red-200' :
                isMine ? 'border-sky-400 bg-sky-500/25 text-sky-100' : 'border-white/25 text-white/70'
              }`}>
                {showCorrect ? <Check size={14} /> : showWrong ? <X size={14} /> : String.fromCharCode(65 + i)}
              </span>
              <span className="text-[15px] leading-snug flex-1">{opt.text}</span>
              {isMine && !revealed && <span className="shrink-0 text-sky-300 text-[10.5px] font-mono-spec uppercase font-bold mt-1.5">Ton vote</span>}
            </button>
          );
        })}
      </div>

      {submitting && <div className="mt-4 text-white/50 text-[13px] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Envoi du vote…</div>}
      {error && <div className="mt-3 rounded-lg border border-red-500/40 bg-red-950/30 p-3 text-red-200 text-[13px]" data-testid="phone-vote-error">{error}</div>}

      {picked !== null && !revealed && (
        <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4 text-center" data-testid="phone-vote-done">
          <Check size={22} className="mx-auto text-emerald-300 mb-1.5" />
          <div className="text-white text-[15px] font-bold">Vote enregistré !</div>
          <div className="text-white/55 text-[12.5px] mt-1">Regarde le vidéoprojecteur — les résultats s&apos;affichent en direct. La bonne réponse sera révélée par ton formateur.</div>
        </div>
      )}

      {revealed && (
        <div className="mt-5 rounded-xl border border-sky-500/40 bg-sky-950/25 p-4" data-testid="phone-vote-revealed">
          {picked !== null && (
            <div className={`text-[15px] font-bold mb-2 ${myAnswerCorrect ? 'text-emerald-300' : 'text-red-300'}`}>
              {myAnswerCorrect ? '🎉 Bonne réponse, bravo !' : 'Raté cette fois — retiens bien :'}
            </div>
          )}
          <div className="flex items-start gap-2.5">
            <Info size={16} className="text-sky-300 mt-0.5 shrink-0" />
            <p className="text-white/85 text-[13.5px] leading-relaxed">{slide.explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
}
